import type { FC } from 'react'
import { useMemo } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { alpha, Box, Tooltip, Typography, useTheme } from '@mui/material'

import type { ClaudeSession, Command, ConversationTurnEntry } from '@/index'
import { formatTimestamp } from '@/common/utils'
import {
  resolveEventTypeColorFor,
  resolveEventTypeLabelFor,
} from '@/features/events/shared/types/enums/EventType'
import { CopyButton, standardTooltipProps } from '@/common/components'

import { extractEventContent } from '../utils'
import { MarkdownRenderer } from '../markdown'
import { FIXED_EVENT_LABEL_SIZE, FIXED_TIMESTAMP_SIZE } from '../shared'
import { DEFAULT_BUBBLE_STYLES } from './defaults'
import { createBubblePaperSx } from './styles'

interface EventBubbleProps {
  readonly event: ConversationTurnEntry
  readonly isAgent: boolean
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly showTimestamp?: boolean
  readonly onEventDetailsClick?: (event: ConversationTurnEntry) => void
}

export const EventBubble: FC<EventBubbleProps> = ({
  event,
  isAgent,
  showTimestamp = true,
  onEventDetailsClick,
}) => {
  const theme = useTheme()
  const eventContent = useMemo(() => extractEventContent(event), [event])
  const borderColor = isAgent
    ? resolveEventTypeColorFor(event.eventType, theme)
    : theme.palette.info.main
  const borderPosition = isAgent ? 'left' : 'right'
  const bubbleConfig = isAgent
    ? DEFAULT_BUBBLE_STYLES.bubbles.agent
    : DEFAULT_BUBBLE_STYLES.bubbles.user

  return (
    <Box sx={eventBubbleContainerSx}>
      <Box sx={createBubblePaperSx(theme, borderColor, borderPosition, bubbleConfig)}>
        <Box sx={eventContentSx}>
          <MarkdownRenderer content={eventContent} />
        </Box>

        <Box className="bubble-footer" sx={eventFooterSx}>
          {onEventDetailsClick ? (
            <Tooltip {...standardTooltipProps} title="Event details">
              <Typography
                className="event-details-link"
                variant="caption"
                onClick={() => onEventDetailsClick(event)}
                sx={clickableEventLabelSx(isAgent, borderColor)}
              >
                {resolveEventTypeLabelFor(event.eventType)}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" sx={eventLabelSx(isAgent, borderColor)}>
              {resolveEventTypeLabelFor(event.eventType)}
            </Typography>
          )}
          <Box className="copy-button" sx={copyButtonContainerSx}>
            <CopyButton text={eventContent} variant="small" />
          </Box>
        </Box>
      </Box>

      {showTimestamp && (
        <Typography variant="caption" color="text.secondary" sx={eventTimestampSx}>
          {formatTimestamp(event.timestamp)}
        </Typography>
      )}
    </Box>
  )
}

const eventBubbleContainerSx: SxProps<Theme> = {
  width: '100%',
  mb: 2,
}

const eventContentSx: SxProps<Theme> = {
  mb: 0,
}

const eventFooterSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  mt: 0,
  maxHeight: 0,
  overflow: 'hidden',
  opacity: 0,
  transition: 'max-height 0.2s ease-in-out, opacity 0.2s ease-in-out, margin-top 0.2s ease-in-out',
}

const eventLabelSx = (isAgent: boolean, color: string): SxProps<Theme> => ({
  fontWeight: 600,
  color: isAgent
    ? alpha(color, DEFAULT_BUBBLE_STYLES.bubbles.agent.borderOpacity)
    : alpha(color, DEFAULT_BUBBLE_STYLES.bubbles.user.borderOpacity),
  fontSize: FIXED_EVENT_LABEL_SIZE,
})

const clickableEventLabelSx = (isAgent: boolean, color: string): SxProps<Theme> => ({
  fontWeight: 600,
  color: isAgent
    ? alpha(color, DEFAULT_BUBBLE_STYLES.bubbles.agent.borderOpacity)
    : alpha(color, DEFAULT_BUBBLE_STYLES.bubbles.user.borderOpacity),
  cursor: 'pointer',
  transition: 'color 0.2s, opacity 0.2s',
  '&:hover': {
    color: color,
  },
  fontSize: FIXED_EVENT_LABEL_SIZE,
})

const eventTimestampSx: SxProps<Theme> = {
  display: 'block',
  mt: 0.5,
  textAlign: 'left',
  fontSize: FIXED_TIMESTAMP_SIZE,
}

const copyButtonContainerSx: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  marginLeft: 'auto',
}
